import { AgentSetup, AllowedDomain, getSetup, updateSetup } from "./setup-store";

// CRM backend for Support Agent > Setup (agent-setup-api.md):
//   GET/POST       /agents/setups              list (?limit&offset) / create
//   PATCH          /agents/setups/{id}         save identity fields
//   POST / DELETE  /agents/setups/{id}/domains[/{domain_id}]
//   POST           /agents/setups/{id}/widget-installation  enable + embed code
//   POST           /files/upload               multipart "file" -> { file_id }
//   GET            /files/{file_id}/download-url
// The page edits one setup per tenant: the newest one, created on first save.
// Reads work for any tenant user; writes need an admin (403 otherwise).
//
// Plain fetch rather than useCrmApi: the store is module-level (not a
// component), and DELETE answers 204 with no body, which useCrmApi's
// unconditional res.json() turns into a fake 500.

const BASE = process.env.REACT_APP_BACKEND_URL || "https://api.crm.dalmlabs.com/api/v1";

interface ApiSetup {
  id: string;
  name: string;
  role: string;
  purpose: string;
  short_intro_message: string;
  chat_window_color: string;
  brand_name?: string;
  accent_color?: string;
  widget_enabled?: boolean;
  inactivity_timeout_minutes?: number;
  profile_image_file_id: string | null;
  brand_logo_file_id: string | null;
  allowed_domains: AllowedDomain[];
}

type Result<T> = { data: T | null; error: string | null };

// `detail` is a string for service errors and a field-error array for 422s.
const errorText = (json: any, fallback: string): string => {
  const d = json?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((e: any) => `${e.loc?.slice(1).join(".") || "field"}: ${e.msg}`).join("; ");
  return fallback;
};

async function call<T>(path: string, init: { method?: string; body?: unknown; form?: FormData } = {}, fallback = "Request failed"): Promise<Result<T>> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Accept: "application/json",
  };
  if (init.body !== undefined) headers["Content-Type"] = "application/json";
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: init.method || "GET",
      headers,
      body: init.form ?? (init.body !== undefined ? JSON.stringify(init.body) : undefined),
    });
    const json = res.status === 204 ? null : await res.json().catch(() => null);
    return res.ok ? { data: json as T, error: null } : { data: null, error: errorText(json, fallback) };
  } catch {
    return { data: null, error: "Couldn't reach the server. Check your connection and try again." };
  }
}

const downloadUrl = async (fileId: string | null): Promise<string> => {
  if (!fileId) return "";
  const { data } = await call<{ download_url: string }>(`/files/${fileId}/download-url`);
  return data?.download_url || "";
};

// API setup -> store fields (images resolved to fresh signed URLs).
const applySetup = async (api: ApiSetup, withImages = true) => {
  const current = getSetup();
  updateSetup({
    setupId: api.id,
    agentName: api.name,
    agentRole: api.role || current.agentRole,
    purpose: api.purpose,
    introMessage: api.short_intro_message,
    windowColor: api.chat_window_color,
    brandName: api.brand_name || "",
    accentColor: api.accent_color || current.accentColor,
    widgetEnabled: !!api.widget_enabled,
    profileImageFileId: api.profile_image_file_id,
    brandLogoFileId: api.brand_logo_file_id,
    domains: api.allowed_domains || [],
    ...(api.inactivity_timeout_minutes !== undefined ? { chatSettingsSupported: true, inactivityTimeoutMinutes: api.inactivity_timeout_minutes } : {}),
  });
  if (!withImages) return;
  const [profileImage, brandLogo] = await Promise.all([downloadUrl(api.profile_image_file_id), downloadUrl(api.brand_logo_file_id)]);
  updateSetup({ profileImage, brandLogo });
};

const identityBody = (s: AgentSetup) => ({
  name: s.agentName.trim(),
  role: s.agentRole,
  purpose: s.purpose,
  short_intro_message: s.introMessage,
  chat_window_color: s.windowColor,
  brand_name: s.brandName.trim(),
  accent_color: s.accentColor,
  profile_image_file_id: s.profileImageFileId,
  brand_logo_file_id: s.brandLogoFileId,
  ...(s.chatSettingsSupported ? { inactivity_timeout_minutes: s.inactivityTimeoutMinutes } : {}),
});

export const loadSetup = async (): Promise<string | null> => {
  const { data, error } = await call<{ items: ApiSetup[] }>("/agents/setups?limit=1&offset=0", {}, "Couldn't load the agent setup.");
  if (error) return error;
  if (data?.items?.[0]) {
    await applySetup(data.items[0]);
    // GET doesn't return the embed code; for a live widget, re-requesting the
    // installation is idempotent (same key) and fills it in.
    if (data.items[0].widget_enabled) await installWidget();
  }
  return null;
};

// Creates the setup on first save, PATCHes it after that.
export const saveIdentity = async (): Promise<string | null> => {
  const s = getSetup();
  const { data, error } = s.setupId
    ? await call<ApiSetup>(`/agents/setups/${s.setupId}`, { method: "PATCH", body: identityBody(s) }, "Couldn't save the agent identity.")
    : await call<ApiSetup>("/agents/setups", { method: "POST", body: identityBody(s) }, "Couldn't save the agent identity.");
  if (error || !data) return error;
  await applySetup(data, false);
  return null;
};

const ensureSetup = async (): Promise<string | null> => (getSetup().setupId ? null : saveIdentity());

// Uploads the image, shows it straight away, and attaches it to the saved
// setup (if there is one yet — otherwise it goes in with the first save).
export const uploadImage = async (kind: "profile" | "logo", file: File, preview: string): Promise<string | null> => {
  const form = new FormData();
  form.append("file", file);
  const { data, error } = await call<{ file_id: string; status: string }>("/files/upload", { method: "POST", form }, "Couldn't upload the image.");
  if (error || !data) return error;
  const field = kind === "profile" ? "profile_image_file_id" : "brand_logo_file_id";
  updateSetup(kind === "profile" ? { profileImageFileId: data.file_id, profileImage: preview } : { brandLogoFileId: data.file_id, brandLogo: preview });
  const { setupId } = getSetup();
  if (!setupId) return null;
  const res = await call<ApiSetup>(`/agents/setups/${setupId}`, { method: "PATCH", body: { [field]: data.file_id } }, "Couldn't attach the image.");
  return res.error;
};

export const addDomain = async (origin: string): Promise<string | null> => {
  const missing = await ensureSetup();
  if (missing) return missing;
  const { setupId, domains } = getSetup();
  const { data, error } = await call<AllowedDomain>(`/agents/setups/${setupId}/domains`, { method: "POST", body: { domain: origin } }, "Couldn't add this domain.");
  if (error || !data) return error;
  updateSetup({ domains: [...domains, data] });
  return null;
};

export const removeDomain = async (domainId: string): Promise<string | null> => {
  const { setupId } = getSetup();
  const { error } = await call(`/agents/setups/${setupId}/domains/${domainId}`, { method: "DELETE" }, "Couldn't remove this domain.");
  if (error) return error;
  updateSetup({ domains: getSetup().domains.filter((d) => d.id !== domainId) });
  return null;
};

// Enables the widget and returns its embed snippet. Idempotent: the same
// public key comes back each time unless `rotate` is set (old key stops).
export const installWidget = async (rotate = false): Promise<string | null> => {
  const missing = await ensureSetup();
  if (missing) return missing;
  const { setupId } = getSetup();
  const { data, error } = await call<{ widget_key: string; script_url: string; embed_code: string }>(
    `/agents/setups/${setupId}/widget-installation`,
    { method: "POST", body: rotate ? { rotate_key: true } : {} },
    "Couldn't generate the install code."
  );
  if (error || !data) return error;
  updateSetup({ widgetEnabled: true, widgetKey: data.widget_key, scriptUrl: data.script_url, embedCode: data.embed_code });
  return null;
};

export const disableWidget = async (): Promise<string | null> => {
  const { setupId } = getSetup();
  if (!setupId) return null;
  const { error } = await call<ApiSetup>(`/agents/setups/${setupId}`, { method: "PATCH", body: { widget_enabled: false } }, "Couldn't turn off the widget.");
  if (error) return error;
  updateSetup({ widgetEnabled: false });
  return null;
};
